import "./SalesPage.css";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type SaleData = {
  id: string;
  organization_id: string;
  customer_id: string | null;
  quotation_id: string | null;
  service_order_id: string | null;
  sale_number: string;
  status: "draft" | "confirmed" | "partially_paid" | "paid" | "cancelled";
  issue_date: string;
  currency_code: string;
  subtotal: number | string;
  discount_total: number | string;
  tax_total: number | string;
  total: number | string;
  paid_total: number | string;
  notes: string | null;
};

type CustomerData = {
  id: string;
  name: string;
  document_type: string | null;
  document_number: string | null;
};

type SaleItem = {
  id: string;
  description: string;
  quantity: number | string;
  unit_price: number | string;
  discount_amount: number | string;
  tax_rate: number | string;
  line_total: number | string;
};

type PaymentRow = {
  id: string;
  amount: number | string;
  payment_method: string;
  reference: string | null;
  notes: string | null;
  paid_at: string;
};

type AccountingDocumentData = {
  id: string;
  full_number: string | null;
  document_type: "invoice" | "receipt" | "internal";
  status: "draft" | "issued" | "cancelled";
  sunat_status: string;
  issue_date: string;
  due_date: string | null;
  total: number | string;
  currency_code: string;
};

type RelatedCommercialDocument = {
  relation_type: string;
  entity_type: string;
  entity_id: string;
  reference: string;
  status: string;
  issue_date: string | null;
  total: number | string | null;
  currency_code: string | null;
};

type SaleDetail = {
  sale: SaleData;
  customer: CustomerData | null;
  items: SaleItem[];
  payments: PaymentRow[];
  accounting_document: AccountingDocumentData | null;
  balance: number | string;
};

const STATUS_LABELS: Record<SaleData["status"], string> = {
  draft: "Borrador",
  confirmed: "Confirmada",
  partially_paid: "Pago parcial",
  paid: "Pagada",
  cancelled: "Cancelada",
};

function relatedStatusLabel(value: string) {
  const labels: Record<string, string> = {
    draft: "Borrador",
    sent: "Enviada",
    viewed: "Vista",
    approved: "Aprobada",
    rejected: "Rechazada",
    expired: "Vencida",
    confirmed: "Confirmada",
    partially_paid: "Pago parcial",
    paid: "Pagada",
    issued: "Emitido",
    cancelled: "Anulado",
    pending: "Pendiente",
    accepted: "Aceptado",
  };
  return labels[value.toLowerCase()] ?? value;
}

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function dateTime(value: string) {
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function SaleDetailPage() {
  const { saleId = "" } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<SaleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const [documentMode, setDocumentMode] = useState<"cash" | "credit">("cash");
  const [documentDueDate, setDocumentDueDate] = useState("");
  const [relatedDocuments, setRelatedDocuments] = useState<RelatedCommercialDocument[]>([]);

  const loadDetail = useCallback(async () => {
    if (!saleId) {
      setLoadError("Venta inválida.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const { data, error } = await supabase.rpc("get_sale_detail", {
      p_sale_id: saleId,
    });

    if (error) {
      setDetail(null);
      setLoadError(error.message);
      setLoading(false);
      return;
    }

    const next = data as SaleDetail;

    if (
      selectedOrganization &&
      next.sale.organization_id !== selectedOrganization.organizationId
    ) {
      setDetail(null);
      setLoadError("La venta no pertenece a la empresa seleccionada.");
      setLoading(false);
      return;
    }

    setDetail(next);

    if (selectedOrganization) {
      const { data: relatedData } = await supabase.rpc(
        "list_related_commercial_documents",
        {
          p_organization_id: selectedOrganization.organizationId,
          p_quotation_id: next.sale.quotation_id ?? null,
          p_sale_id: next.sale.id,
          p_purchase_order_id: null,
        },
      );
      setRelatedDocuments((relatedData ?? []) as RelatedCommercialDocument[]);
    }
    setLoading(false);
  }, [saleId, selectedOrganization]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  async function changeStatus(status: "confirmed" | "cancelled") {
    setBusy(status);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("transition_sale_status", {
      p_sale_id: saleId,
      p_new_status: status,
    });

    setBusy(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess(
      status === "confirmed" ? "Venta confirmada." : "Venta cancelada.",
    );
    await loadDetail();
  }

  async function prepareAccountingDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!detail) return;

    if (documentMode === "credit" && !documentDueDate) {
      setActionError("Define la fecha de vencimiento para la venta a crédito.");
      return;
    }

    setBusy("accounting-document");
    setActionError(null);
    setActionSuccess(null);

    const { data, error } = await supabase.rpc(
      "prepare_accounting_document_from_sale",
      {
        p_sale_id: saleId,
        p_on_credit: documentMode === "credit",
        p_due_date: documentMode === "credit" ? documentDueDate : null,
      },
    );

    setBusy(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    const result = data as {
      already_existed?: boolean;
      full_number?: string | null;
    };

    setActionSuccess(
      result.already_existed
        ? "La venta ya tenía un comprobante contable."
        : "Comprobante borrador creado. Revísalo en Contabilidad antes de emitir.",
    );
    await loadDetail();
  }


  if (loading) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando venta…</strong>
        </section>
      </div>
    );
  }

  if (loadError || !detail) {
    return (
      <div className="page">
        <Link className="compact-button sales-back" to="/ventas">
          ← Volver a Ventas
        </Link>
        <section className="empty-state empty-state-error">
          <strong>No se pudo abrir la venta.</strong>
          <p>{loadError ?? "Venta no disponible."}</p>
        </section>
      </div>
    );
  }

  const { sale, customer, items, payments, accounting_document, balance } = detail;
  const canPrepareAccountingDocument =
    !accounting_document &&
    ["confirmed", "partially_paid", "paid"].includes(sale.status);

  return (
    <div className="page">
      <div className="sales-detail-topline">
        <Link className="compact-button" to="/ventas">
          ← Volver a Ventas
        </Link>
        <span className={`status-badge sale-status-${sale.status}`}>
          {STATUS_LABELS[sale.status]}
        </span>
      </div>

      <header className="page-header sales-detail-header">
        <div>
          <span className="eyebrow">Venta</span>
          <h1>{sale.sale_number}</h1>
          <p>
            {customer ? (
              <Link className="record-inline-link" to={`/clientes/${customer.id}`}>
                {customer.name}
              </Link>
            ) : (
              "Sin cliente"
            )}
          </p>
        </div>
        <div className="sales-detail-total">
          <span>Total</span>
          <strong>{money(sale.total, sale.currency_code)}</strong>
        </div>
      </header>

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success sales-message">{actionSuccess}</div>
      ) : null}

      <section className="sales-summary-grid">
        <div className="metric-card">
          <span>Subtotal</span>
          <strong>{money(sale.subtotal, sale.currency_code)}</strong>
        </div>
        <div className="metric-card">
          <span>IGV</span>
          <strong>{money(sale.tax_total, sale.currency_code)}</strong>
        </div>
        <div className="metric-card">
          <span>Pagado</span>
          <strong>{money(sale.paid_total, sale.currency_code)}</strong>
        </div>
        <div className="metric-card">
          <span>Saldo</span>
          <strong>{money(balance, sale.currency_code)}</strong>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Trazabilidad comercial</span>
            <h2>Documentos vinculados</h2>
          </div>
        </div>

        <div className="sale-related-grid">
          {relatedDocuments.map((item) => {
            const label =
              item.relation_type === "cotizacion"
                ? "Cotización"
                : item.relation_type === "venta"
                  ? "Venta"
                  : item.relation_type === "nota_credito"
                    ? "Nota de crédito"
                    : item.relation_type === "nota_debito"
                      ? "Nota de débito"
                      : "Comprobante de venta";

            const to =
              item.entity_type === "quotation"
                ? `/cotizaciones/${item.entity_id}`
                : item.entity_type === "sale"
                  ? `/ventas/${item.entity_id}`
                  : "/contabilidad";

            return (
              <article className="sale-related-card" key={item.entity_type + item.entity_id}>
                <span className="status-badge">{label}</span>
                <strong>{item.reference}</strong>
                <span>{relatedStatusLabel(item.status)}</span>
                <Link className="compact-button" to={to}>
                  Ver detalle
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Comprobante</span>
            <h2>Documento contable</h2>
          </div>
        </div>

        {accounting_document ? (
          <div className="sale-accounting-document">
            <div>
              <span className="status-badge">{accounting_document.status}</span>
              <strong>{accounting_document.full_number ?? "Sin número"}</strong>
              <small>
                {accounting_document.document_type} · Estado fiscal{" "}
                {accounting_document.sunat_status}
              </small>
            </div>
            <Link className="compact-button" to="/contabilidad">
              Abrir Contabilidad
            </Link>
          </div>
        ) : canPrepareAccountingDocument ? (
          <form
            className="sale-accounting-form"
            onSubmit={prepareAccountingDocument}
          >
            <label>
              Condición
              <select
                value={documentMode}
                onChange={(event) =>
                  setDocumentMode(event.target.value as "cash" | "credit")
                }
              >
                <option value="cash">Contado</option>
                <option value="credit">Crédito</option>
              </select>
            </label>

            {documentMode === "credit" ? (
              <label>
                Vencimiento
                <input
                  type="date"
                  min={sale.issue_date}
                  required
                  value={documentDueDate}
                  onChange={(event) => setDocumentDueDate(event.target.value)}
                />
              </label>
            ) : null}

            <div className="form-actions sales-payment-action">
              <button
                className="primary-button"
                type="submit"
                disabled={busy === "accounting-document"}
              >
                {busy === "accounting-document"
                  ? "Creando…"
                  : "Crear comprobante borrador"}
              </button>
            </div>
          </form>
        ) : (
          <p className="settings-empty">
            Confirma la venta antes de generar el comprobante.
          </p>
        )}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Detalle</span>
            <h2>Conceptos</h2>
          </div>
        </div>

        <div className="sale-detail-list">
          {items.map((item) => (
            <div className="sale-detail-row" key={item.id}>
              <div>
                <strong>{item.description}</strong>
                <span>
                  {Number(item.quantity)} × {money(item.unit_price, sale.currency_code)}
                  {Number(item.discount_amount) > 0
                    ? ` · Desc. ${money(item.discount_amount, sale.currency_code)}`
                    : ""}
                  {Number(item.tax_rate) > 0 ? ` · IGV ${Number(item.tax_rate)}%` : ""}
                </span>
              </div>
              <strong>{money(item.line_total, sale.currency_code)}</strong>
            </div>
          ))}
        </div>

        {sale.status === "draft" ? (
          <div className="quotation-actions">
            <button
              className="compact-button"
              type="button"
              disabled={busy === "confirmed"}
              onClick={() => void changeStatus("confirmed")}
            >
              {busy === "confirmed" ? "Confirmando…" : "Confirmar venta"}
            </button>
            <button
              className="compact-button compact-button-danger"
              type="button"
              disabled={busy === "cancelled"}
              onClick={() => void changeStatus("cancelled")}
            >
              Cancelar
            </button>
          </div>
        ) : null}

        {sale.status === "confirmed" && Number(sale.paid_total) === 0 ? (
          <div className="quotation-actions">
            <button
              className="compact-button compact-button-danger"
              type="button"
              disabled={busy === "cancelled"}
              onClick={() => void changeStatus("cancelled")}
            >
              Cancelar venta
            </button>
          </div>
        ) : null}
      </section>

      {sale.status !== "cancelled" ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Cobranza</span>
              <h2>Cobro centralizado</h2>
            </div>
          </div>

          {Number(balance) <= 0 ? (
            <div className="form-success sales-message">
              La venta no tiene saldo pendiente.
            </div>
          ) : !accounting_document ? (
            <p className="settings-empty">
              Crea primero el comprobante borrador. Después de emitirlo, la
              cuenta por cobrar se sincronizará automáticamente con Tesorería.
            </p>
          ) : accounting_document.status === "draft" ? (
            <div className="sale-accounting-document">
              <div>
                <strong>Comprobante pendiente de emisión</strong>
                <small>
                  Emite el comprobante para habilitar la cobranza contable.
                </small>
              </div>
              <Link className="compact-button" to="/contabilidad">
                Emitir en Contabilidad
              </Link>
            </div>
          ) : accounting_document.status === "issued" ? (
            <div className="sale-accounting-document">
              <div>
                <strong>
                  Saldo por cobrar {money(balance, sale.currency_code)}
                </strong>
                <small>
                  Los cobros se registran en Tesorería para actualizar CxC,
                  caja/banco y contabilidad en una sola operación.
                </small>
              </div>
              <Link className="primary-button" to="/tesoreria">
                Cobrar en Tesorería
              </Link>
            </div>
          ) : (
            <p className="settings-empty">
              El comprobante está cancelado. No se permiten nuevos cobros.
            </p>
          )}
        </section>
      ) : null}

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Pagos históricos</span>
            <h2>Registro operativo previo</h2>
          </div>
        </div>

        <div className="sale-detail-list">
          {payments.length === 0 ? (
            <p className="settings-empty">
              Sin pagos operativos anteriores. Los nuevos cobros se registran
              desde Tesorería.
            </p>
          ) : (
            payments.map((payment) => (
              <div className="sale-detail-row" key={payment.id}>
                <div>
                  <strong>{payment.payment_method}</strong>
                  <span>
                    {dateTime(payment.paid_at)}
                    {payment.reference ? ` · ${payment.reference}` : ""}
                  </span>
                </div>
                <strong>{money(payment.amount, sale.currency_code)}</strong>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
